import React from 'react';
import { VirtualizedFormSelect } from '@roger/ui-components';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../../store';
import {
  getAgencyClinicDisplayName,
  type AgencyClinicDisplayInput,
} from '../../utils/agencyClinicDisplayName';
import {
  getClinicRogerOnlyPillLabel,
  shouldShowClinicEmrPills,
} from '../../utils/clinicEmr';
import { ALL_CLINICS } from '../../hooks/useAccessibleClinics';
import {
  compareClinicOptions,
  type ClinicOption,
} from '../../utils/clinicSelect';
import { ClinicEmrBadge } from './ClinicEmrBadge';

type VirtualizedFormSelectProps = React.ComponentProps<
  typeof VirtualizedFormSelect
>;

export type ClinicSelectClinic = AgencyClinicDisplayInput & {
  clinic_source?: string | null;
  is_kinnser?: boolean;
};

export interface ClinicSelectProps extends Omit<
  VirtualizedFormSelectProps,
  'options' | 'formatOptionLabel'
> {
  clinics: ClinicSelectClinic[];
  /** Filter UIs: prepend an All Clinics option */
  includeAllOption?: boolean;
  allOptionValue?: string;
  allOptionLabel?: string;
  /** Override agency gate (tests / forced hide). Default: both integration flags. */
  showEmrPills?: boolean;
  agencyName?: string | null;
}

/**
 * Shared agency-clinic picker. Owns display-name fallbacks and a "Roger Only"
 * pill when the agency has both integrations enabled.
 *
 * Prefer this over hand-rolling clinic `SelectItem` / option maps.
 */
export const ClinicSelect = React.forwardRef<
  HTMLInputElement,
  ClinicSelectProps
>(function ClinicSelect(
  {
    clinics,
    includeAllOption = false,
    allOptionValue = ALL_CLINICS,
    allOptionLabel = 'All Clinics',
    showEmrPills: showEmrPillsProp,
    agencyName,
    ...rest
  },
  ref
) {
  const agency = useStore(useShallow(state => state.user?.agency));
  const showEmrPills =
    showEmrPillsProp ?? shouldShowClinicEmrPills(agency ?? null);

  const clinicByValue = React.useMemo(() => {
    const map = new Map<string, ClinicSelectClinic>();
    for (const clinic of clinics) {
      map.set(clinic.id, clinic);
    }
    return map;
  }, [clinics]);

  const options: ClinicOption[] = React.useMemo(() => {
    const totalClinicCount = clinics.length;
    const clinicOptions = clinics
      .map(clinic => ({
        value: clinic.id,
        label: getAgencyClinicDisplayName(clinic, {
          agencyName,
          totalClinicCount,
        }),
      }))
      .sort(compareClinicOptions);
    if (!includeAllOption) return clinicOptions;
    // Keep All first; clinics above are already A–Z.
    return [{ value: allOptionValue, label: allOptionLabel }, ...clinicOptions];
  }, [clinics, includeAllOption, allOptionValue, allOptionLabel, agencyName]);

  const formatOptionLabel = React.useCallback(
    (
      option: { value: string; label: string },
      _meta: { context: 'menu' | 'value' }
    ) => {
      if (!showEmrPills || option.value === allOptionValue) {
        return option.label;
      }
      const clinic = clinicByValue.get(option.value);
      if (!getClinicRogerOnlyPillLabel(clinic)) {
        return option.label;
      }
      return (
        <span className='flex min-w-0 items-center gap-1.5'>
          <span className='truncate'>{option.label}</span>
          <ClinicEmrBadge clinic={clinic} />
        </span>
      );
    },
    [showEmrPills, allOptionValue, clinicByValue]
  );

  return (
    <VirtualizedFormSelect
      ref={ref}
      options={options}
      formatOptionLabel={showEmrPills ? formatOptionLabel : undefined}
      // Options are pre-sorted (All pinned first when present).
      sortOptions={false}
      {...rest}
    />
  );
});

ClinicSelect.displayName = 'ClinicSelect';
